import React, { useState } from 'react';
import { setManualLocation, clearLocationCache, getPrayerTimes } from '../server';

/**
 * LocationInput Component
 *
 * Allows users to manually enter their location if:
 * - Geolocation is not supported
 * - User denied location permission
 * - IP-based detection failed or is inaccurate
 *
 * Usage: Import and render this component when location detection fails
 */
function LocationInput({ onLocationSet }) {
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [error, setError] = useState('');
  const [isHovered, setIsHovered] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const formatDate = (date) => date.toLocaleDateString('en-GB').split('/').join('-');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!city.trim() || !country.trim()) {
      setError('Please enter both city and country');
      return;
    }

    setSubmitting(true);
    setError('Verifying location...');

    try {
      // Verify location by testing prayer times API
      const today = formatDate(new Date());
      const testResult = await getPrayerTimes({
        city: city.trim(),
        country: country.trim(),
        date: today
      });

      if (!testResult || !testResult.timings) {
        setError('Location not found. Please check spelling and try again. Use full country name (e.g., "United States" not "US")');
        setSubmitting(false);
        return;
      }

      // Location is valid, save it
      const success = setManualLocation({
        city: city.trim(),
        country: country.trim()
      });

      if (success) {
        setError('');
        // Clear prayer timings cache so new location will trigger fresh fetch
        try {
          localStorage.removeItem('prayerTimings');
          localStorage.removeItem('lastFetchDate');
        } catch (err) {
          console.warn('Could not clear cache:', err);
        }

        console.log('Location verified and set successfully, reloading...');

        // Small delay to ensure localStorage writes complete
        setTimeout(() => {
          if (onLocationSet) {
            onLocationSet({ city: city.trim(), country: country.trim() });
          }
        }, 100);
      } else {
        setError('Failed to save location. Please try again.');
        setSubmitting(false);
      }
    } catch (error) {
      console.error('Error verifying location:', error);
      setError('Could not verify location. Check your spelling or try a different city.');
      setSubmitting(false);
    }
  };

  const handleUseMyLocation = () => {
    clearLocationCache();
    window.location.reload(); // Reload to trigger location detection again
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h3 style={styles.title}>Set Your Location</h3>
        <p style={styles.description}>
          We couldn't detect your location automatically. Please enter your city and country.
        </p>

        <form onSubmit={handleSubmit} style={styles.form}>
          <input
            type="text"
            placeholder="City (e.g., New York)"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            style={styles.input}
            className="location-input"
          />

          <input
            type="text"
            placeholder="Country (e.g., United States)"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            style={styles.input}
            className="location-input"
          />

          {error && <p style={styles.error}>{error}</p>}

          <button
            type="submit"
            disabled={submitting}
            style={{
              ...styles.button,
              ...(isHovered && !submitting && styles.buttonHover),
              ...(submitting && { opacity: 0.6, cursor: 'not-allowed' })
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {submitting ? 'Loading...' : 'Set Location'}
          </button>
        </form>

        <button
          onClick={handleUseMyLocation}
          style={styles.linkButton}
        >
          Try detecting my location again
        </button>
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '60vh',
    padding: '20px',
  },
  card: {
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    borderRadius: '8px',
    border: '1px solid #e25933',
    padding: '30px',
    maxWidth: '500px',
    width: '100%',
    boxShadow: '0 4px 20px rgba(146, 60, 181, 0.3)',
  },
  title: {
    margin: '0 0 10px 0',
    fontSize: '1.55rem',
    color: '#e25933',
  },
  description: {
    margin: '0 0 20px 0',
    fontSize: '1rem',
    color: '#e4a9a9',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
  },
  input: {
    padding: '12px',
    fontSize: '16px',
    border: '1px solid #e4a9a9',
    borderRadius: '4px',
    outline: 'none',
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    color: '#e4a9a9',
    fontFamily: 'Poppins, sans-serif',
  },
  button: {
    padding: '12px',
    fontSize: '16px',
    backgroundColor: 'transparent',
    color: '#e25933',
    border: '1px solid #e25933',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontFamily: 'Poppins, sans-serif',
    transition: 'all 0.3s ease',
  },
  buttonHover: {
    backgroundColor: '#e25933',
    color: '#000000',
    transform: 'scale(1.05)',
  },
  linkButton: {
    marginTop: '15px',
    padding: '8px',
    fontSize: '14px',
    backgroundColor: 'transparent',
    color: '#a0eced',
    border: 'none',
    cursor: 'pointer',
    textDecoration: 'underline',
    fontFamily: 'Poppins, sans-serif',
  },
  error: {
    color: '#e25933',
    fontSize: '14px',
    margin: '0',
  },
};

export default LocationInput;
